import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Theme } from '@yandex-int/k-common/typings';
import { Spin } from 'platform-components';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath, Redirect } from 'react-router-dom';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { getCurrentClass } from 'common.components/user/selectors';
import * as themesSelectors from 'store/themes/selectors';
import { doesThemeExist as doesThemeExistUtil } from 'store/themes/utils';

import { ReduxTeacherStore } from '../../../../typings';
import SectionPage from '../../../library.section-page/section-page';
import SubjectPage from '../../../library.subject-page/subject-page';
import ThemePage from '../../../library.theme-page/theme-page';

interface StoreProps {
    doesThemeExist: boolean;
    theme?: Theme;
    themes: Array<Theme>;
    actualThemes: Array<Theme>;
    loadingStatus: LOADING_STATUSES;
    isActualTheme: boolean;

    shouldLoadActualThemes: boolean;
    actualThemesLoadingStatus: LOADING_STATUSES;
}

interface HOCProps extends WithRouterPathProps {}

interface Props extends StoreProps, HOCProps {}

export class LibraryPages extends React.Component<Props> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    doesThemeExist = () => {
        const {
            doesThemeExist,
            match: { params },
            actualThemes,
            isActualTheme,
        } = this.props;
        const themeId = Number(params.themeId);

        if (isActualTheme) {
            return doesThemeExistUtil(themeId, actualThemes);
        }

        return doesThemeExist;
    };

    isActualThemePage = (): boolean => {
        const {
            isActualTheme,
            location: { pathname },
        } = this.props;
        const { staticUrls } = this.context;

        return isActualTheme || Boolean(matchPath(pathname, { path: staticUrls.labLibraryActualThemes, exact: true }));
    };

    hasActualThemesToLoad = (): boolean => {
        const { actualThemesLoadingStatus, shouldLoadActualThemes } = this.props;

        return (
            actualThemesLoadingStatus === LOADING_STATUSES.LOADING ||
            (this.isActualThemePage() &&
                shouldLoadActualThemes &&
                actualThemesLoadingStatus === LOADING_STATUSES.UNSENT)
        );
    };

    isLoading = (): boolean => {
        const { loadingStatus } = this.props;

        return (
            loadingStatus === LOADING_STATUSES.LOADING ||
            loadingStatus === LOADING_STATUSES.UNSENT ||
            this.hasActualThemesToLoad()
        );
    };

    shouldRedirectToStacksPage = (): boolean => {
        const {
            location: { pathname },
            theme,
        } = this.props;
        const { staticUrls } = this.context;

        const isThemePage = [staticUrls.labLibrarySubjectTheme, staticUrls.deepLabTheme].some((path) =>
            matchPath(pathname, { path, exact: true })
        );

        return (isThemePage && theme?.is_stack) || false;
    };

    hasChildThemes = (): boolean => {
        const {
            match: { params },
            themes,
        } = this.props;

        const themeId = Number(params.themeId);

        return themes.some(({ parent, is_stack: isStack }) => parent === themeId && !isStack);
    };

    render = () => {
        const {
            getRouterPath,
            match: { params },
        } = this.props;
        const { staticUrls } = this.context;
        const themeId = Number(params.themeId);
        const teamId = Number(params.classId);

        if (!themeId) {
            return <SubjectPage />;
        }

        if (this.isLoading()) {
            return <Spin center />;
        }

        if (!this.doesThemeExist()) {
            const url = teamId ? staticUrls.labLibrarySubject : staticUrls.deepLabLibrary;

            return <Redirect to={`${getRouterPath(url, { teamId })}`} />;
        }

        if (this.shouldRedirectToStacksPage()) {
            const stacksPath = teamId ? staticUrls.labLibrarySubjectThemeStacks : staticUrls.deepLabStacks;

            return <Redirect to={getRouterPath(stacksPath)} />;
        }

        if (!this.hasChildThemes()) {
            return <ThemePage />;
        }

        return <SectionPage />;
    };
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: HOCProps): StoreProps => {
    const {
        location: { search },
        match: {
            params: { subject, themeId: thId, classId },
        },
    } = ownProps;
    const query = qs.parse(search);
    const themeId = Number(thId);
    const currentTeam = getCurrentClass(storeState, { classId });
    const courseId = getCurrentYearCourseId(storeState);
    const grade = currentTeam?.level || Number(query.grade) || null;

    return {
        doesThemeExist: themesSelectors.getDoesThemeExist(storeState, { subject, grade, themeId }),
        themes: themesSelectors.getThemes(storeState, { subject, grade }) || [],
        actualThemes: themesSelectors.getActualThemes(storeState, { subject, courseId }),
        theme: themesSelectors.getThemeById(storeState, { themeId, subject, grade }),
        loadingStatus: themesSelectors.getThemesTreeLoadingStatus(storeState, { subject, themeId, grade }),
        isActualTheme: themesSelectors.getIsActualTheme(storeState, { themeId }),
        shouldLoadActualThemes: themesSelectors.getShouldLoadActualThemes(storeState, { classId, subject, courseId }),
        actualThemesLoadingStatus: themesSelectors.getActualThemesLoadingStatus(storeState, { subject, courseId }),
    };
};

export default withRouterPath(connect(mapStateToProps)(LibraryPages));
